import * as THREE from 'three'
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import { viewerApi } from './viewerApi'
import { useAppStore } from '@core/store'
import {
  extractSpatialOrigins,
  spatialOriginsReady,
  spatialTypeLabel,
} from './spatialPlacements'
import { linhasDeCoordenadas } from './origemCoordenadas'
import { coordFrameFor, worldToIfcAxes } from '@viewer/measure/coordReadout'

/** As duas leituras de um ponto: no ficheiro (já em metros) e, quando o
 *  mundo está georreferenciado, no CRS (lida do ponto do mundo). */
function coordenadasDoPonto(
  noFicheiro: [number, number, number],
  mundo: THREE.Vector3,
): string[] {
  const s = useAppStore.getState()
  const frame = coordFrameFor(
    s.models.map((m) => m.id),
    s.geoBaselines,
    s.geoOverrides,
  )
  const crs = frame === 'georref' ? worldToIfcAxes([mundo.x, mundo.y, mundo.z]) : null
  return linhasDeCoordenadas(noFicheiro, crs)
}

// ---------------------------------------------------------------------------
// Origin marker overlay.
//
// Draws a small axes tripod + label at:
//   1. the WORLD origin (0,0,0) da cena; and
//   2. each model's TRUE IFC-file origin. Fragments recentra a geometria
//      de modelos com offsets grandes (baked no placement) e devolve a
//      translação aplicada em model.getCoordinates(): a origem do
//      ficheiro fica em matrixWorld × translação, NÃO em
//      model.object.position. Sem essa correção o marcador aparecia na
//      geometria em vez de na origem do IFC.
//
// A etiqueta de cada modelo é CLICÁVEL: abre o editor de posição desse
// modelo no sidebar (via store.requestPlacementEditor).
//
// Axis colours follow the map/IFC convention shown in the position editor:
//   E (Eastings, +X three) vermelho · N (Northings, -Z three) verde ·
//   h (altura, +Y three) azul.
// ---------------------------------------------------------------------------

const GROUP_NAME = '__vn2rOriginMarker'
const AXIS_LEN = 5 // metres (origem do IFC / da cena)
const NODE_AXIS_LEN = 1.5 // metres (tripés dos nós espaciais)

const ZERO_BOX = new THREE.Box3(
  new THREE.Vector3(0, 0, 0),
  new THREE.Vector3(0, 0, 0),
)
const ZERO_SPHERE = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 0)

function pinBounds(geom: THREE.BufferGeometry): void {
  geom.boundingBox = ZERO_BOX.clone()
  geom.boundingSphere = ZERO_SPHERE.clone()
}

function clearGroup(): void {
  const world = viewerApi.getWorld()
  if (!world) return
  const scene = world.scene.three
  const g = scene.getObjectByName(GROUP_NAME)
  if (!g) return
  g.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.geometry) m.geometry.dispose()
    if (m.material) {
      const mat = m.material
      if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose())
      else (mat as THREE.Material).dispose()
    }
    const maybeCss2d = o as { element?: unknown }
    if (
      maybeCss2d.element instanceof HTMLElement &&
      maybeCss2d.element.parentNode
    ) {
      maybeCss2d.element.parentNode.removeChild(maybeCss2d.element)
    }
  })
  scene.remove(g)
}

function makeLabel(
  text: string,
  color: string,
  opts?: { title?: string; onClick?: () => void },
): CSS2DObject {
  const div = document.createElement('div')
  div.className = 'vn2r-origin-label'
  div.textContent = text
  div.style.color = color
  div.style.fontFamily = 'var(--font-sans, system-ui, sans-serif)'
  div.style.fontSize = '11px'
  div.style.fontWeight = '600'
  div.style.padding = '1px 5px'
  div.style.background = 'rgba(11, 11, 12, 0.78)'
  div.style.border = '1px solid ' + color
  div.style.borderRadius = '3px'
  // 'pre': as etiquetas de uma origem em foco levam as coordenadas em
  // linhas próprias (separadas por \n).
  div.style.whiteSpace = 'pre'
  div.style.lineHeight = '1.35'
  div.style.userSelect = 'none'
  if (opts?.onClick) {
    div.style.pointerEvents = 'auto'
    div.style.cursor = 'pointer'
    div.addEventListener('click', (e) => {
      e.stopPropagation()
      opts.onClick!()
    })
  } else {
    div.style.pointerEvents = 'none'
  }
  if (opts?.title) div.title = opts.title
  return new CSS2DObject(div)
}

function axisLine(
  dir: [number, number, number],
  hex: number,
  len: number,
): THREE.LineSegments {
  const geom = new THREE.BufferGeometry()
  geom.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(
      [0, 0, 0, dir[0] * len, dir[1] * len, dir[2] * len],
      3,
    ),
  )
  pinBounds(geom)
  const mat = new THREE.LineBasicMaterial({
    color: hex,
    transparent: true,
    opacity: 0.95,
    depthTest: false,
  })
  const line = new THREE.LineSegments(geom, mat)
  line.frustumCulled = false
  line.renderOrder = 1001
  return line
}

/** Tripod: E (+X, red), h (+Y, blue), N (-Z, green). Axis tags só quando
 *  `withTags` (as etiquetas podem ser desligadas no filtro de origens). */
function makeTripod(len = AXIS_LEN, withTags = true): THREE.Group {
  const g = new THREE.Group()
  g.add(axisLine([1, 0, 0], 0xef4444, len)) // E
  g.add(axisLine([0, 1, 0], 0x3b82f6, len)) // h (up)
  g.add(axisLine([0, 0, -1], 0x22c55e, len)) // N
  if (withTags) {
    const tagE = makeLabel('E', '#f87171')
    tagE.position.set(len, 0, 0)
    const tagH = makeLabel('h', '#93c5fd')
    tagH.position.set(0, len, 0)
    const tagN = makeLabel('N', '#4ade80')
    tagN.position.set(0, 0, -len)
    g.add(tagE, tagH, tagN)
  }
  return g
}

/** Recentre translation the converter applied to this model's geometry
 *  (three local frame). Zero when the model was not recentred. */
async function recentreShift(
  model: NonNullable<ReturnType<typeof viewerApi.getModel>>,
): Promise<THREE.Vector3> {
  try {
    const coords = await model.getCoordinates()
    if (Array.isArray(coords) && coords.length >= 3) {
      return new THREE.Vector3(coords[0], coords[1], coords[2])
    }
  } catch {
    /* sem coordinates */
  }
  return new THREE.Vector3(0, 0, 0)
}

/** Converte um ponto em coordenadas do FICHEIRO IFC (x=E, y=N, z=h, em
 *  metros) para coordenadas do MUNDO three, aplicando o recentramento do
 *  conversor e o transform geo do object. */
function filePointToWorld(
  model: NonNullable<ReturnType<typeof viewerApi.getModel>>,
  shift: THREE.Vector3,
  p: [number, number, number],
): THREE.Vector3 {
  // IFC (x,y,z) -> three (x, z, -y), depois o shift de recentramento e
  // o matrixWorld do object (geo placement).
  const local = new THREE.Vector3(p[0], p[2], -p[1]).add(shift)
  model.object.updateMatrixWorld(true)
  return local.applyMatrix4(model.object.matrixWorld)
}

// Reentrância: chamadas sobrepostas (toggle + efeito de store) cancelam
// as builds anteriores em vez de entrelaçarem clears/adds.
let buildToken = 0

/** Redraw (or remove) the origin marker overlay. Call with the current
 *  toggle state; safe to call repeatedly (models loaded, geo edited). */
export async function applyOriginMarkerOverlay(
  visible: boolean,
): Promise<void> {
  const myToken = ++buildToken
  clearGroup()
  if (!visible) {
    viewerApi._invalidate()
    return
  }
  const world = viewerApi.getWorld()
  if (!world) return
  const scene = world.scene.three
  const group = new THREE.Group()
  group.name = GROUP_NAME
  group.renderOrder = 1000
  group.raycast = () => {}
  scene.add(group)

  const kinds = useAppStore.getState().originMarkerKinds
  // Uma origem em foco ("Ir"): só essa se desenha, com etiqueta e
  // coordenadas, ignorando o filtro de categorias.
  const foco = useAppStore.getState().originMarkerFocus
  const KIND_BY_TYPE: Record<string, keyof typeof kinds> = {
    IFCSITE: 'site',
    IFCBUILDING: 'building',
    IFCBUILDINGSTOREY: 'storey',
    IFCSPACE: 'space',
  }

  // 1. Origem da cena three.js (0,0,0): conceito interno, desligada por
  // defeito no filtro; útil só para depurar o recentramento do conversor.
  if (kinds.world && !foco) {
    const worldTripod = makeTripod(AXIS_LEN, kinds.labels)
    if (kinds.labels) {
      const worldLabel = makeLabel('Origem da cena (0,0,0)', '#e5e7eb', {
        title:
          'Origem interna da cena 3D. Não é necessariamente a origem do ' +
          'ficheiro IFC: o conversor recentra modelos com coordenadas grandes.',
      })
      worldLabel.position.set(0, AXIS_LEN * 1.35, 0)
      worldTripod.add(worldLabel)
    }
    group.add(worldTripod)
  }

  // 2. Per-model TRUE file origins + origens da hierarquia espacial.
  const state = useAppStore.getState()
  for (const [modelId, model] of viewerApi.iterModels()) {
    if (foco && foco.modelId !== modelId) continue
    const shift = await recentreShift(model)
    if (myToken !== buildToken) return // superseded by a newer call
    const origin = filePointToWorld(model, shift, [0, 0, 0])
    const meta = state.models.find((m) => m.id === modelId)
    const placement =
      state.geoOverrides[modelId] ?? state.geoBaselines[modelId] ?? null
    const focoNoFicheiro = foco?.chave === 'ficheiro'
    // Se coincide com a origem do mundo, o tripé de cima já a marca
    // (salvo em foco: aí o tripé da cena não se desenha).
    if (focoNoFicheiro || (!foco && kinds.file && origin.length() >= 0.005)) {
      const comEtiqueta = kinds.labels || focoNoFicheiro
      const tripod = makeTripod(AXIS_LEN, comEtiqueta)
      tripod.position.copy(origin)
      tripod.rotation.copy(model.object.rotation)
      if (comEtiqueta) {
        const detail = placement
          ? `E=${placement.eastings.toFixed(3)}  N=${placement.northings.toFixed(3)}  ` +
            `h=${placement.orthogonalHeight.toFixed(3)}  heading=${placement.rotationDeg.toFixed(2)}°`
          : 'sem MapConversion/override'
        const titulo = `Origem IFC: ${meta?.fileName ?? modelId}`
        const label = makeLabel(
          focoNoFicheiro
            ? [titulo, ...coordenadasDoPonto([0, 0, 0], origin)].join('\n')
            : titulo,
          '#fbbf24',
          {
            title:
              `Origem do ficheiro IFC (0,0,0 do ficheiro, incluindo o recentramento ` +
              `do conversor e o MapConversion/override). ${detail}. ` +
              `Clica para abrir o editor de posição deste modelo.`,
            onClick: () => {
              useAppStore.getState().requestPlacementEditor(modelId)
            },
          },
        )
        label.position.set(0, AXIS_LEN * 1.35, 0)
        tripod.add(label)
      }
      group.add(tripod)
    }

    // Origens EXATAS dos nós espaciais (Site/Building/Storey/Space),
    // lidas do STEP via parser paralelo. Só quando o parser já está
    // carregado: não disparamos um re-parse pesado só pelo toggle;
    // o Info do modelo > "Origens da hierarquia" carrega-o on-demand.
    if (focoNoFicheiro) continue
    const focoNoNo = typeof foco?.chave === 'number' ? foco.chave : null
    if (
      focoNoNo === null &&
      !kinds.site && !kinds.building && !kinds.storey && !kinds.space
    ) {
      continue
    }
    if (!spatialOriginsReady(modelId)) continue
    try {
      const res = await extractSpatialOrigins(modelId)
      if (myToken !== buildToken) return
      if (!res) continue
      for (const node of res.items) {
        if (focoNoNo !== null) {
          if (node.expressID !== focoNoNo) continue
        } else {
          const kindKey = KIND_BY_TYPE[node.ifcType]
          if (!kindKey || !kinds[kindKey]) continue
        }
        const w = filePointToWorld(model, shift, node.origin)
        // Tripé pequeno (mesma linguagem visual da origem do IFC) + ponto
        // ciano no vértice para distinguir nós espaciais.
        const nodeTripod = makeTripod(NODE_AXIS_LEN, false)
        nodeTripod.position.copy(w)
        nodeTripod.rotation.copy(model.object.rotation)
        const dotGeom = new THREE.SphereGeometry(0.18, 12, 8)
        pinBounds(dotGeom)
        const dot = new THREE.Mesh(
          dotGeom,
          new THREE.MeshBasicMaterial({ color: 0x22d3ee, depthTest: false }),
        )
        dot.renderOrder = 1002
        dot.frustumCulled = false
        dot.raycast = () => {}
        nodeTripod.add(dot)
        if (kinds.labels || focoNoNo !== null) {
          const nome = `${spatialTypeLabel(node.ifcType)}${node.name ? ' · ' + node.name : ''}`
          const lbl = makeLabel(
            focoNoNo !== null
              ? [nome, ...coordenadasDoPonto(node.origin, w)].join('\n')
              : nome,
            '#67e8f9',
            {
              title:
                `Origem EXATA do IfcLocalPlacement (lida do ficheiro): ` +
                `E=${node.origin[0].toFixed(3)}  N=${node.origin[1].toFixed(3)}  ` +
                `h=${node.origin[2].toFixed(3)} m` +
                (node.relative
                  ? `  ·  relativa ao pai: [${node.relative.map((v) => v.toFixed(3)).join(', ')}]`
                  : '') +
                `. Clica para selecionar este nó.`,
              onClick: () => {
                void viewerApi.selectByLocalId(modelId, node.expressID)
              },
            },
          )
          lbl.position.set(0, NODE_AXIS_LEN * 1.2, 0)
          nodeTripod.add(lbl)
        }
        group.add(nodeTripod)
      }
    } catch {
      /* extração falhou: marcadores de nós ficam de fora */
    }
  }
  viewerApi._invalidate()
}

/** Raio da esfera que a câmara enquadra ao ir para uma origem: perto o
 *  bastante para ler o tripé (5 m) e a etiqueta, longe o bastante para
 *  ver onde fica em relação ao modelo à volta. */
const RAIO_AO_IR = 12

/** Leva a câmara a uma origem e deixa-a isolada no marcador: a do
 *  ficheiro IFC de um modelo ('ficheiro') ou a de um nó espacial (o
 *  expressID). Liga o marcador se estiver desligado. Devolve false
 *  quando o modelo ou o nó já não existem. */
export async function irParaOrigem(
  modelId: string,
  chave: 'ficheiro' | number,
  rotulo: string,
): Promise<boolean> {
  const model = viewerApi.getModel(modelId)
  const world = viewerApi.getWorld()
  if (!model || !world) return false
  let noFicheiro: [number, number, number] = [0, 0, 0]
  if (chave !== 'ficheiro') {
    const res = await extractSpatialOrigins(modelId)
    const no = res?.items.find((n) => n.expressID === chave)
    if (!no) return false
    noFicheiro = no.origin
  }
  const shift = await recentreShift(model)
  const ponto = filePointToWorld(model, shift, noFicheiro)
  const store = useAppStore.getState()
  store.setOriginMarkerFocus({ modelId, chave, rotulo })
  if (!store.originMarkerOverlay) store.setOriginMarkerOverlay(true)
  // fitToSphere serve às duas câmaras (perspectiva e ortográfica) e
  // mantém a direcção de vista: só aproxima e centra.
  try {
    await world.camera.controls.fitToSphere(new THREE.Sphere(ponto, RAIO_AO_IR), true)
  } catch (err) {
    console.warn('[originMarker] fitToSphere falhou', err)
  }
  viewerApi._invalidate()
  return true
}
